import React, { useState, useMemo, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "./aggrid.css";

import DefaultLoader from "./DefaultLoader";
import TableControls from "./TableControls";
import CustomHeaderComponent from "./CustomHeaderComponent";
import { useLocalStorage } from "../utils/localstorage";

// Register modules
ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * @typedef {Object} CustomColumnDef
 * @property {string} field - Field name
 * @property {string} headerName - Header label
 * @property {boolean} [sortable=false] - Whether the column supports server-side sorting
 * @property {boolean} [filterable=false] - Whether the column supports server-side filtering
 * @property {"text"|"select"|"datetime"|"workflowName"} [filterType="text"] - Filter UI type
 * @property {Array} [filterOptions] - Dropdown options if filterType="select"
 * @property {boolean} [keywordField=false] - Adds ".keyword" suffix for server sort
 * @property {function} [cellRenderer]
 * @property {function} [tooltipValueGetter]
 */

const AgGridTable = React.memo(
  ({
    tableId = null,
    columnDefs = [],
    rowData = [],
    defaultColDef,
    defaultVisibleColumns = [],
    gridOptions,
    containerStyle,
    headerContent,
    customPaginationComponent,
    handleRefreshClick,
    loading = false,
    error = null,
    enableRowSelection = false,
    onRowSelectionChanged = () => {},
    selectedRows = [],
    bulkActionComponent = null,
    currentSort = "",
    onSortChanged = () => {},
    onFilterChanged = () => {},
    currentFilters = {},
    resetServerFilters = false,
    handleResetFilters = () => {},
    onClearSelectedRows = () => {},
    onBulkActionComplete = () => {},
    ...restProps
  }) => {
    const [gridApi, setGridApi] = useState();
    const [isResettingFilters, setIsResettingFilters] = useState(false);

    const columnStorageKey = useMemo(
      () => (tableId ? `ag-grid-${tableId}-columns` : null),
      [tableId]
    );

    const [selectedColumns, setSelectedColumns] = useLocalStorage(
      columnStorageKey,
      defaultVisibleColumns
    );

    const handleGridReady = useCallback((params) => {
      setGridApi(params.api);
    }, []);

    const handleServerResetFilters = useCallback(() => {
      if (gridApi) {
        gridApi.setFilterModel(null);
        gridApi.resetColumnState();
      }
      setIsResettingFilters(true);
      handleResetFilters();
    }, [handleResetFilters, gridApi]);

    useEffect(() => {
      if (!isResettingFilters) return;
      const timeoutId = setTimeout(() => setIsResettingFilters(false), 500);
      return () => clearTimeout(timeoutId);
    }, [isResettingFilters]);

    const filteredColumns = useMemo(
      () => columnDefs.filter((col) => selectedColumns.includes(col.field)),
      [columnDefs, selectedColumns]
    );

    const processedColumnDefs = useMemo(() => {
      return filteredColumns.map((col) => ({
        ...col,
        headerComponent: CustomHeaderComponent,
        headerComponentParams: {
          columnConfig: col,
          currentSort,
          currentFilters,
          onSortChanged,
          onFilterChanged,
        },
      }));
    }, [
      filteredColumns,
      currentSort,
      currentFilters,
      onSortChanged,
      onFilterChanged,
    ]);

    const handleSelectionChanged = useCallback(() => {
      if (gridApi && enableRowSelection) {
        const selectedNodes = gridApi.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        onRowSelectionChanged(selectedData);
      }
    }, [gridApi, onRowSelectionChanged, enableRowSelection]);

    useEffect(() => {
      if (gridApi && enableRowSelection) {
        if (selectedRows && selectedRows.length > 0) {
          const selectedIds = selectedRows.map((row) => row.workflowId);
          gridApi.forEachNode((node) => {
            const shouldBeSelected =
              node.data && selectedIds.includes(node.data.workflowId);
            node.setSelected(shouldBeSelected);
          });
        } else {
          gridApi.deselectAll();
        }
      }
    }, [gridApi, selectedRows, enableRowSelection, rowData]);

    const rowSelectionConfig = useMemo(() => {
      return enableRowSelection ? { mode: "multiRow" } : undefined;
    }, [enableRowSelection]);

    const isRowSelectionEnabled = useMemo(
      () => enableRowSelection === true || enableRowSelection === "True",
      [enableRowSelection]
    );

    useEffect(() => {
      if (gridApi) {
        if (error) gridApi.showNoRowsOverlay();
        else if (loading) gridApi.showLoadingOverlay();
        else if (rowData?.length > 0) gridApi.hideOverlay();
        else gridApi.showNoRowsOverlay();
      }
    }, [loading, error, gridApi, rowData]);

    return (
      <div className="server-side-datatable">
        <div className="table-header">
          <TableControls
            resetFiltersFunction={handleServerResetFilters}
            currentFilters={currentFilters}
            handleRefreshClick={handleRefreshClick}
            columnDefs={columnDefs}
            selectedColumns={selectedColumns}
            defaultVisibleColumns={defaultVisibleColumns}
            onColumnSelectionChange={setSelectedColumns}
            GlobalSearch={restProps?.GlobalSearch}
            enableRowSelection={enableRowSelection}
            selectedRows={selectedRows}
            bulkActionComponent={bulkActionComponent}
            actionButton={restProps?.actionButton}
            onClearSelectedRows={onClearSelectedRows}
            onBulkActionComplete={onBulkActionComplete}
          />
        </div>

        <div className="custom-ag-table-container" style={containerStyle}>
          <AgGridReact
            style={{ width: "100%", height: "100%" }}
            rowData={rowData}
            columnDefs={processedColumnDefs}
            defaultColDef={defaultColDef}
            headerHeight={36}
            rowHeight={36}
            onGridReady={handleGridReady}
            gridOptions={gridOptions}
            getRowId={
              isRowSelectionEnabled
                ? (params) => params?.data?.workflowId
                : undefined
            }
            rowSelection={rowSelectionConfig}
            suppressColumnVirtualisation={true}
            onSelectionChanged={
              enableRowSelection ? handleSelectionChanged : undefined
            }
            suppressDragLeaveHidesColumns={true}
            {...restProps}
          />
        </div>
      </div>
    );
  }
);

export default AgGridTable;
